// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { utils } from '@seedplanner/shared-utils';
// biome-ignore lint/style/useImportType: <explanation>
import * as React from 'react';
import { compareTasks, formatStateOrStatus, formatTaskName, getID } from '../../utils';
import { VisibleAtContainer } from '../common/containers/VisibleAtContainer';
import { Table } from '../common/table';
import type { ColumnType } from '../common/table/Table';
import { Time } from '../common/time';
import { CropIdLink } from '../crop';
import { OrderIdLink } from '../order';
import { PlantingGroupIdLink } from '../plantingGroup';
import TaskGroupIdLink from './TaskGroupIdLink';
import TaskIdLink from './TaskIdLink';

export interface TaskData {
  id: string;
  task_name: string;
  task_status: string;
  task_time: number;
  planting?: {
    id: string;
    order_id?: string | null;
    trays_rounded: string;
  } | null;
  crop?: {
    id: string;
    crop_name: string;
  } | null;
}
interface Props {
  readonly tasks: TaskData[];
  readonly useIndividualTaskLinks?: boolean;
  readonly showOrder?: boolean;
  readonly hidePlantingColumns?: boolean;
}

export default function TaskTable({
  tasks,
  useIndividualTaskLinks = false,
  showOrder = true,
  hidePlantingColumns = false,
}: Props) {
  // Sort tasks by time and name
  const tasksForSort = [...tasks];
  tasksForSort.sort((a, b) => {
    return compareTasks(a, b);
  });
  const taskIdValues: React.ReactElement[] = [];
  const taskNameValues: string[] = [];
  const statusValues: string[] = [];
  const plantingLinkValues: (React.ReactElement | string)[] = [];
  const timeValues: React.ReactElement[] = [];
  const cropLinkValues: (React.ReactElement | string)[] = [];
  const orderLinkValues: (React.ReactElement | string)[] = [];
  const trayCountValues: (number | string)[] = [];
  for (const task of tasksForSort) {
    taskIdValues.push(
      useIndividualTaskLinks ? (
        <VisibleAtContainer
          up={<TaskIdLink id={getID(task.id)} title={formatTaskName(getID(task.id), false)} />}
          down={<TaskIdLink id={getID(task.id)} title={formatTaskName(getID(task.id))} />}
        />
      ) : (
        <TaskGroupIdLink id={getID(task.id)} />
      ),
    );
    taskNameValues.push(task.task_name);
    statusValues.push(formatStateOrStatus(task.task_status));
    plantingLinkValues.push(task.planting ? <PlantingGroupIdLink id={getID(task.planting.id)} /> : '');
    timeValues.push(
      <VisibleAtContainer
        up={<Time time={task.task_time} />}
        down={<Time time={task.task_time} options={{ compactDisplay: true }} />}
      />,
    );
    cropLinkValues.push(task.crop ? <CropIdLink id={getID(task.crop.id)} name={task.crop.crop_name} /> : '');
    if (task.planting?.order_id) {
      orderLinkValues.push(<OrderIdLink id={task.planting.order_id} />);
    } else {
      orderLinkValues.push('');
    }
    trayCountValues.push(task.planting?.trays_rounded ?? '');
  }
  const columns: ColumnType[] = [
    {
      name: 'Task ID',
      smallName: 'ID',
      values: taskIdValues,
    },
    {
      name: 'Date',
      values: timeValues,
    },
    {
      name: 'Name',
      values: taskNameValues,
    },
    !hidePlantingColumns
      ? {
          name: 'Crop',
          values: cropLinkValues,
        }
      : undefined,
    !hidePlantingColumns
      ? {
          name: 'Trays',
          values: trayCountValues,
          numeric: true,
          visibleAt: 'sm' as const,
        }
      : undefined,
    {
      name: 'Status',
      values: statusValues,
      visibleAt: 'sm' as const,
    },
    !hidePlantingColumns
      ? {
          name: 'Planting',
          values: plantingLinkValues,
          visibleAt: 'md' as const,
        }
      : undefined,
    showOrder && !hidePlantingColumns
      ? {
          name: 'Order',
          values: orderLinkValues,
          visibleAt: 'lg' as const,
        }
      : undefined,
  ].filter(utils.notNull);

  return <Table columns={columns} />;
}
